import i18next from 'i18next';
import { get } from 'lodash';

import { updateSegment } from 'common/api/v1/segmentsLayout';
import {
    SEGMENT_COLOR,
    SEGMENT_STROKE_COLOR,
} from 'common/components/maps/constants';
import { setCeilZone } from 'common/components/segment/asceHelpers';
import {
    POLYGON_MAP,
    POLYGON_OBSTACLE,
    POLYGON_SEGMENT,
    POLYGON_RECTANGLE,
    POLYGON_CIRCLE,
} from 'common/components/segment/constants';
import {
    buildGroups,
    formatSegment,
    getDictionaryById,
    getModulesGroupV2,
    markGroupEdges,
    prepareModulesGroups,
} from 'common/components/segment/helpers';
import {
    getSegmentArea,
    getSegmentDiameter,
    showReponseErrorsAsAlert,
} from 'common/utils/helpers';
import {
    getMinMaxCols,
    getMinMaxRows,
} from 'common/utils/helpers/systemsValues';
import showToast from 'common/utils/showToast';

import { getNewModules, getObstaceZIndex } from '../helpers';
import * as selectors from '../selectors';

import handleCollition from './handleCollition';
import updateSegments from './updateSegments';

const buidlSegmentPayload = ({ config, segments, selectedSegment }) => {
    const { solar_modules_number } = selectedSegment;
    const { commercialOfferId, google, mapValue, polygon } = config;

    let assigned = 0;
    let ceilZonedGroups = [];
    let defaultOfferPanelValues = {
        color: SEGMENT_COLOR,
        id: null,
        stroke_color: SEGMENT_STROKE_COLOR,
    };
    let groups = [];
    let module = null;
    let newPanel = null;
    let solar_modules = [];

    const {
        algorithm,
        azimuth,
        beam_spacing,
        building_heading,
        building_height,
        category,
        ceil_tilt,
        col_spacing,
        drawing_type,
        fill_algorithm,
        frame_col_spacing,
        frame_cols_number,
        frame_row_spacing,
        frame_rows_number,
        front_base_height,
        gclamp_type,
        height,
        is_custom_module,
        lfoot_supports_width,
        name,
        orientation,
        outer_rail_length,
        panel,
        parent_field_segment,
        plate_support_length,
        rail_spacing,
        rail_support_length,
        row_spacing,
        safe_zone,
        seam_distance,
        sheet_gauge,
        sloof_type,
        solar_module,
        support_length,
        system,
        system_uid,
        tilt,
        type_application,
        zIndex,
    } = selectedSegment;

    const quantity =
        fill_algorithm === 'fill' ? Infinity : solar_modules_number;

    if (panel) module = panel;

    if (is_custom_module) {
        module = panel;
        newPanel = {
            ...module,
            brand: module.brandName,
            code: module.name,
            max_power: module.watts,
        };
    }

    if (module) {
        defaultOfferPanelValues = {
            ...defaultOfferPanelValues,
            ...module,
        };
    }

    const { color, stroke_color } = defaultOfferPanelValues;

    const solarCharacterization = {
        assigned,
        azimuth,
        ceil_tilt,
        col_spacing,
        field_segment: selectedSegment.id,
        frame_col_spacing,
        frame_cols_number,
        frame_row_spacing,
        frame_rows_number,
        height,
        orientation,
        polygon: JSON.stringify(polygon),
        quantity,
        row_spacing,
        safe_zone,
        solar_module,
        tilt,
    };

    if (quantity > 0) {
        const obstacles = segments.filter(
            (segment) => segment.type === POLYGON_OBSTACLE,
        );

        const [minCols, maxCols] = getMinMaxCols(system_uid, panel.width);
        const [minRows, maxRows] = getMinMaxRows(system_uid);

        const { boxes, grouped } = getNewModules({
            algorithm,
            building_heading: building_heading,
            data: {
                ...solarCharacterization,
                minRows,
                minCols,
                maxRows,
                maxCols,
            },
            google,
            map: mapValue,
            module,
            obstacles,
            sloof_type,
        });

        let newGrouped = grouped;

        if (fill_algorithm === 'fill') {
            const newBoxes = boxes.map((box) => ({ ...box, enable: true }));
            newGrouped = buildGroups(newBoxes);
        }

        const modules_groups = getModulesGroupV2(newGrouped);
        groups = prepareModulesGroups(modules_groups);

        if (drawing_type === POLYGON_RECTANGLE) {
            const markedGroupEdges = markGroupEdges(modules_groups);

            ceilZonedGroups = setCeilZone({
                buildingHeight: building_height,
                ceilTilt: ceil_tilt,
                google,
                groups: markedGroupEdges,
                heading: building_heading,
                polygon,
                sloofType: sloof_type,
            });
        }

        solar_modules = boxes.map((module) => ({
            cell: module.cell,
            col: module.col,
            group: module.group,
            is_enable: fill_algorithm === 'fill' ? true : module.enable,
            row: module.row,
            solar_module_points: JSON.stringify(
                module.paths.map((point) => ({
                    x: point[1],
                    y: point[0],
                })),
            ),
        }));
    }

    const field_segments = [
        {
            name,
            polygon: polygon.map((point) => ({
                x_coordinate: point[1],
                y_coordinate: point[0],
            })),
            solar_modules,
        },
    ];

    const solution_fields = {
        beam_spacing,
        front_base_height,
        lfoot_supports_width,
        outer_rail_length,
        plate_support_length,
        rail_spacing,
        rail_support_length,
        seam_distance,
        sheet_gauge,
        support_length,
    };

    const payload = {
        ...selectedSegment,
        asce_metadata: ceilZonedGroups,
        commercial_offer: commercialOfferId,
        custom_inputs: {
            ...solution_fields,
            algorithm,
            building_heading,
            building_height,
            category,
            ceil_tilt,
            drawing_type,
            fill_algorithm,
            gclamp_type,
            panel_orientation: orientation,
            sloof_type,
            system_uid,
            tilt,
            type_application,
        },
        field_segments,
        groups,
        name,
        panel: newPanel,
        parent_field_segment,
        polygon,
        segment_color: color,
        segment_stroke_color: stroke_color,
        solar_modules,
        system,
        total_area: getSegmentArea(google, polygon),
        zindex: zIndex,
    };

    return payload;
};

const buildObstaclePayload = ({ config, segments, selectedSegment }) => {
    const { commercialOfferId, google, polygon } = config;

    const {
        height,
        name,
        parent_field_segment,
        safe_zone,
        segment_color,
        segment_stroke_color,
    } = selectedSegment;

    const solarCharacterization = {
        azimuth: 0,
        col_spacing: 0,
        frame_col_spacing: 0,
        frame_cols_number: 0,
        frame_row_spacing: 0,
        frame_rows_number: 0,
        orientation: 'P',
        row_spacing: 0,
        tilt: 0,
    };

    const zIndex = getObstaceZIndex({
        google,
        polygon,
        segments,
        selectedSegment,
    });

    const field_segments = [
        {
            name,
            polygon: polygon.map((point) => ({
                x_coordinate: point[1],
                y_coordinate: point[0],
            })),
        },
    ];

    const drawing_type = selectedSegment.drawing_type;

    let custom_inputs = { drawing_type };

    let diameter = 0;
    if (drawing_type === POLYGON_CIRCLE) {
        diameter = getSegmentDiameter(google, polygon);
        custom_inputs = { ...custom_inputs, diameter };
    }

    const payload = {
        ...selectedSegment,
        commercial_offer: commercialOfferId,
        custom_inputs: custom_inputs,
        field_segments,
        height,
        parent_field_segment,
        safe_zone: parseFloat(safe_zone) || 0,
        segment_color,
        segment_stroke_color,
        total_area: getSegmentArea(google, polygon),
        zindex: zIndex,
        ...solarCharacterization,
    };

    return payload;
};

const buildImagePayload = ({ config, selectedSegment }) => {
    const { commercialOfferId, google, polygon } = config;
    const { name, parent_field_segment, image } = selectedSegment;

    const solarCharacterization = {
        azimuth: 0,
        col_spacing: 0,
        frame_col_spacing: 0,
        frame_cols_number: 0,
        frame_row_spacing: 0,
        frame_rows_number: 0,
        orientation: 'P',
        row_spacing: 0,
        tilt: 0,
    };

    const field_segments = [
        {
            name,
            polygon: polygon.map((point) => ({
                x_coordinate: point[1],
                y_coordinate: point[0],
            })),
        },
    ];

    const payload = {
        ...selectedSegment,
        commercial_offer: commercialOfferId,
        field_segments,
        image,
        parent_field_segment,
        total_area: getSegmentArea(google, polygon),
        ...solarCharacterization,
    };

    return payload;
};

export default (config) => (dispatch, getState) => {
    const {
        callback,
        callbackError,
        commercialOfferId,
        google,
        initialValues,
        mapValue,
        segmentId,
    } = config;

    const state = getState();
    const segments = selectors.getSegmentsData(state);
    const segmentsDictionary = getDictionaryById(segments);
    const selectedSegment = segmentsDictionary[segmentId];
    const { parent_field_segment, type } = selectedSegment;

    let payload = null;

    if (type === POLYGON_SEGMENT) {
        payload = buidlSegmentPayload({ config, segments, selectedSegment });
    }

    if (type === POLYGON_OBSTACLE) {
        payload = buildObstaclePayload({
            config,
            segments,
            selectedSegment,
        });
    }

    if (type === POLYGON_MAP) {
        payload = buildImagePayload({ config, selectedSegment });
    }

    updateSegment(segmentId, payload)
        .then((response) => {
            const segmentData = get(response, 'data', {});
            const formatedSegment = formatSegment(
                segmentData,
                parent_field_segment,
            );
            const updatedSegment = formatedSegment;

            if (type === POLYGON_OBSTACLE && payload.prev_location) {
                updatedSegment.prev_location = payload.prev_location;
            }

            const updatedSegments = segments.map((segment) =>
                segment.id === updatedSegment.id
                    ? { ...segment, ...updatedSegment, open: true }
                    : { ...segment, open: false },
            );
            dispatch(updateSegments(updatedSegments));

            showToast({ body: i18next.t('Segment changes have been saved') });

            if (type === POLYGON_SEGMENT && callback)
                callback(segmentId, formatedSegment.solar_modules);

            if (type === POLYGON_OBSTACLE) {
                dispatch(
                    handleCollition({
                        callback,
                        callbackError,
                        commercialOfferId,
                        google,
                        mapValue,
                        obstacle: updatedSegment,
                    }),
                );
            }
        })
        .catch((error) => {
            if (callbackError) callbackError(segmentId, initialValues);
            showReponseErrorsAsAlert(dispatch, error.response);
        });
};
